import World from './webgl/World'
import SceneManager from './webgl/SceneManager'
import BookManager from './webgl/BookManager'
import Screen from './webgl/Screen'

import { getSceneCount } from './helpers/DataHelper'
import { createRenderTexture } from './helpers/3DEngineHelper'
import {
  getInkMat,
  getRenderTextureMat,
  registerMaterial,
  replaceMeshMaterials,
  overrideMat,
  getBgMat
} from './helpers/MaterialHelper'

import { ease, Easing } from './helpers/EaseHelper'
import { Mouse } from './helpers/MouseHelper'
/**
 * class WebGLManager
 */

const CHAPTER_TIME = 4000
const DELAY_CLOSE_BOOK = 0
const DURATION_FADE_OUT = 4000
const DELAY_FADE_OUT = 2400
const DURATION_FADE_IN = 2000
const DELAY_FADE_IN = 2000
const DELAY_INTRO_CAM = 6000
class WebGLManager {
  /**
   * constructor
   */
  constructor (canvas) {
    this.world = new World(canvas)
    this.sceneManager = new SceneManager()
    this.bookManager = new BookManager()
    this.renderScene = createRenderTexture()
    this.renderBook = createRenderTexture()
    this.screen = new Screen(this.renderBook.texture)
    this.width = 0
    this.height = 0
    this.postprocessing = true
    this.loadingValue = 0
    this.transition = true
    this.displayChapter = () => {}
    this.hideChapter = () => {}
    this.displaySceneHandler = () => {}
    this.hideSceneHandler = () => {}
    this.onBookOpen = () => {}
    this.onBookClose = () => {}
    this.mouseMotionEnabled = true
    this.renderFlag = true
    this._bookFlag = true
    this._bookOpenFlag = true
    this.bookPosition = { x: 0, y: 0 }

    registerMaterial(getInkMat('Black-Backface'))
    registerMaterial(getInkMat('Black-Backface.001'))
    registerMaterial(getInkMat('Black-Backface.002'))
    registerMaterial(getRenderTextureMat('SCENE-3D', this.renderScene))
  }

  get bookFlag () { return this._bookFlag}
  set bookFlag (value) {
    this._bookFlag = value
    this.screen.setTexture( value ? this.renderBook.texture : this.renderScene.texture)
    this.resize()
  }

  get bookOpenFlag () { return this._bookOpenFlag}
  set bookOpenFlag (value) {
    this._bookOpenFlag = value
    if (value) {
      this.bookManager.openBook()
      this.onBookOpen()
    } else {
      setTimeout(() => {
        this.bookManager.closeBook()
        this.onBookClose()
      }, DELAY_CLOSE_BOOK)
    }
  }

  addLoading (onStart, onComplete, duration, delay) {
    ease(this.screen)
      .to({ loadingOpacity: 1 }, this.transition ? duration : 0)
      .delay(delay)
      .easing(Easing.Cubic.InOut)
      .onStart(() => onStart ? onStart() : {})
      .onComplete(() => onComplete ? onComplete() : {})
      .start()
  }

  removeLoading (onStart, onComplete) {
    ease(this.screen)
      .to({loadingOpacity:0}, this.transition ? DURATION_FADE_IN : 0)
      .delay(DELAY_FADE_IN)
      .easing(Easing.Cubic.InOut)
      .onStart(() => onStart ? onStart() : {})
      .onComplete(() => onComplete ? onComplete() : {})
      .start()
  }

  loadRequiredAssets (loadingHandler) {
    return new Promise((resolve, reject) => {
      this.bookManager.loadBook(loadingHandler).then(book => {resolve()})
    })
  }

  preloadAll (loadingHandler) {
    return new Promise(resolve => {
      let countLoading = getSceneCount() + 1
      let loadLoaded, loadTotal
      const loadings = []
      const loadingUpdate = index => (progress, loaded, total) => {
        loadings[index] = {progress, loaded, total}
        loadLoaded = loadings.reduce((acc, obj) => acc + parseFloat(obj.loaded), 0)
        loadTotal = loadings.reduce((acc, obj) => acc + parseFloat(obj.total), 0)
        loadingHandler(loadLoaded / loadTotal || 0, loadLoaded, loadTotal)
      }
      const checkLoading = () => (--countLoading === 0) ? resolve() : null
      for (let count = 0; count < (countLoading - 1); count++) {
        this.preloadScene(count, loadingUpdate(count)).then(id => {checkLoading()})
      }
      this.bookManager.loadBook(loadingUpdate(5)).then(book => {checkLoading()})
    })
  }

  preloadScene (id, loadingHandler) {
    return new Promise(resolve => {
      const preload = id => {
        this.sceneManager.loadSceneId(id, (progress, loaded, total) => { loadingHandler(progress, loaded, total) })
          .then(scene => { resolve(id) })
      }
      preload(id)
    })
  }

  displayScene (id) {
    return new Promise(resolve => {
      const loadingHandler = progress => { this.loadingValue = progress }
      const onAdd = mesh => { replaceMeshMaterials(mesh) }
      const onComplete = id => {
        this.renderFlag = true
        this.hideChapter(id)
        this.removeLoading(() => {
          this.displaySceneHandler(id)
        })
        resolve()
      }
      const onOutroComplete = delay => {
        this.addLoading(null, () => {
          this.loadingValue = 0
          display(id)
          this.displayChapter(id)
          this.renderFlag = false
        }, delay > 0 ? DURATION_FADE_OUT : 0, delay)
      }
      const display = id => {
        this.sceneManager.displayScene(id, loadingHandler, onAdd, null, DELAY_INTRO_CAM)
        .then(id => {
          this.loadingValue = 1
          setTimeout(() => onComplete(id), CHAPTER_TIME)
        })
      }
      if (this.sceneManager.current >= 0) {
        this.hideSceneHandler()
        this.sceneManager.hideCurrentScene()
        onOutroComplete(DELAY_FADE_OUT)
      } else {
        onOutroComplete(0)
      }
    })
  }

  displaySceneCut (id) {
    return new Promise(resolve => {
      const onAdd = mesh => { replaceMeshMaterials(mesh) }
      this.sceneManager.displayScene(id, null, onAdd, null, 0)
        .then(id => {
          this.displaySceneHandler(id)
          resolve()
        })
    })
  }

  displayNext() {
    if (this.sceneManager.current + 1 < getSceneCount()) {
      this.displayScene(this.sceneManager.current + 1)
    } else {
      this._bookOpenFlag = false
      this.bookManager.closeBook()
      this.onBookClose(true)
    }
  }

  autoPlay() { this.sceneManager.play(() => setTimeout(() => this.displayNext(), 1000)) }

  switchMat () {
    overrideMat(getBgMat());
    overrideMat(getInkMat('Black-Backface'), 'Black-Backface')
    overrideMat(getInkMat('Black-Backface.001'), 'Black-Backface.001')
    overrideMat(getInkMat('Black-Backface.002'), 'Black-Backface.002')
  }

  resize () {
    this.world.resize()
    this.width = this.world.width
    this.height = this.world.height
    this.renderScene.setSize(this.width, this.height)
    this.renderBook.setSize(this.width, this.height)
    if (this.bookFlag && (this.width / this.height < 4.98 / 3.55)) {
      this.sceneManager.setSize( 4.98, 3.55)
    } else {
      this.sceneManager.setSize(this.width, this.height)
    }
    this.bookManager.setSize(this.width, this.height)
    this.screen.setSize(this.width, this.height)
  }

  render (time, delta) {
    if (!this.renderFlag) return
    if (this.postprocessing) {
      if (this.loadingValue === 1) {
        this.world.render(time, this.sceneManager.scene3D, this.sceneManager.camera, this.renderScene)
        this.world.render(time, this.bookManager.scene3D, this.bookManager.camera, this.renderBook)
      }
      this.screen.update(time)
      this.world.render(time, this.screen.scene3D, this.screen.camera);
    } else if (this.bookFlag) {
      this.world.render(time, this.sceneManager.scene3D, this.sceneManager.camera, this.renderScene)
      this.world.render(time, this.bookManager.scene3D, this.bookManager.camera)
    } else {
      this.world.render(time, this.sceneManager.scene3D, this.sceneManager.camera)
    }
    if (this.mouseMotionEnabled) {
      this.sceneManager.motionCam.update(time, delta, Mouse.rx, Mouse.ry)
      this.bookManager.motionCam.update(time, delta)
      if (this.bookManager.book) {
        this.bookPosition.x += (-Mouse.rx * (1 - this.bookManager.book.openRatio) - this.bookPosition.x) * 0.07
        this.bookPosition.y += (Mouse.ry * (1 - this.bookManager.book.openRatio) - this.bookPosition.y) * 0.07
        this.bookManager.container.position.x = this.bookPosition.x
        this.bookManager.container.position.y = this.bookPosition.y
      }
    }
  }
}
export default WebGLManager


// WEBPACK FOOTER //
// ./src/scripts/WebGLManager.js